import { money, moneyFromMajor, classifyBreakdownRow } from '@shared/lib';
import type { PendingExpenseDto } from './schemas';
import type { PendingExpense, PendingExpenseLocation, PendingRecurrence } from '../model/types';

const RECUR_FREQS = ['daily', 'weekly', 'monthly', 'yearly'] as const;

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
}

const str = (v: unknown): string => (typeof v === 'string' ? v : '');
const numOr = (v: unknown, fallback: number): number =>
  typeof v === 'number' && Number.isFinite(v) ? v : fallback;

/** Map a `reminder_days_before` day-count → the edit form's reminder option. */
function reminderFromDays(days: number): PendingRecurrence['reminder'] {
  if (days === 1) return 'oneDay';
  if (days === 2) return 'twoDays';
  if (days >= 7) return 'oneWeek';
  return 'none';
}

/**
 * Parse a recurrence value → the edit-form recurrence shape (null when there's no usable rule). The
 * value's key + field names vary across the API, so read every field DEFENSIVELY (with fallbacks)
 * from an unknown value — a differing/absent shape yields null instead of throwing.
 */
function toPendingRecurrence(raw: unknown): PendingRecurrence | null {
  const r = asRecord(raw);
  if (!r) return null;
  const freq = (str(r.frequency) || str(r.freq) || str(r.repeat_frequency)).toLowerCase();
  if (!(RECUR_FREQS as readonly string[]).includes(freq)) return null;
  const startStr = str(r.start_date) || str(r.startDate) || str(r.start);
  return {
    frequency: freq as PendingRecurrence['frequency'],
    every: Math.max(1, numOr(r.interval ?? r.every, 1)),
    date: startStr ? new Date(startStr) : null,
    reminder: reminderFromDays(numOr(r.reminder_days_before ?? r.reminderDaysBefore, 0)),
  };
}

/** Parse the DTO's flat `location*` fields into `PendingExpenseLocation` — null when unset. */
function toPendingLocation(dto: PendingExpenseDto): PendingExpenseLocation | null {
  const lat = typeof dto.location_lat === 'string' ? Number(dto.location_lat) : dto.location_lat;
  const long =
    typeof dto.location_long === 'string' ? Number(dto.location_long) : dto.location_long;
  if (!lat || !long || !Number.isFinite(lat) || !Number.isFinite(long)) return null;
  const formattedAddress = dto.location ?? '';
  const address1 = dto.location_address1 ?? '';
  // Prefer the formatted address's first segment (matches entities/location's own name derivation
  // for a place with no separate POI name) — the API doesn't send a distinct place-name field.
  const firstSegment = formattedAddress.split(',')[0]?.trim() ?? '';
  const name = firstSegment !== '' ? firstSegment : address1;
  return {
    name,
    formattedAddress,
    address1,
    address2: dto.location_address2 ?? '',
    city: dto.location_city ?? '',
    state: dto.location_state ?? '',
    zip: dto.location_zip ?? '',
    country: dto.location_country ?? '',
    lat,
    long,
  };
}

/**
 * Anti-corruption: pending-expense DTO → domain. The two attention reasons are independent: a row
 * is "incomplete" when its (last) pending status says so, and "no group" when it's unassigned —
 * both can be true at once. Amounts arrive in MAJOR units here (₹22 → 2200 minor).
 */
export function toPendingExpense(dto: PendingExpenseDto): PendingExpense {
  const amount = moneyFromMajor(dto.amount ?? 0);
  const description = dto.description ?? '';
  const hasDate = Boolean(dto.date);
  // Original bill values (for prefilling edits): when converted, the top-level amount/currency are
  // the settled HOME values, so prefer `expense_amount`/`expense_currency` (the entered values).
  const converted = dto.is_currency_converted && dto.expense_amount != null;
  const billAmount = moneyFromMajor(converted ? (dto.expense_amount ?? 0) : (dto.amount ?? 0));
  const billCurrencyCode =
    (converted ? dto.expense_currency?.currency : undefined) ?? dto.currency_code ?? '';
  // A row "needs details" (incomplete) only when it's actually missing an amount, a description, or a
  // date — derived from the fields so the badge matches what the table shows (the server's
  // pending_status can lag behind edits). When amount + description + date are all present, it's done.
  const isIncomplete = amount <= 0 || description.trim() === '' || !hasDate;
  return {
    id: dto.id,
    date: dto.date ? new Date(dto.date) : null,
    description,
    amount,
    currencySymbol: dto.currency_symbol ?? '',
    currencyCode: dto.currency_code ?? '',
    billAmount,
    billCurrencyCode,
    isConverted: converted,
    // Accept either `payments[]` (ledger shape, id key `user_id`) or `paid_by[]` (create shape, id
    // key `id`) — whichever the GET echoes; each leg's id can be under either key.
    paidBy: (dto.payments ?? dto.paid_by ?? [])
      .map((p) => ({
        userId: p.user_id ?? p.id ?? '',
        type: p.type === 'wallet' ? ('wallet' as const) : ('user' as const),
      }))
      .filter((p) => p.userId !== ''),
    // Split config (amounts MAJOR here → minor for the share); prefills the edit form's Split tab.
    split: (dto.split_configs ?? [])
      .filter((s) => s.user_id)
      .map((s) => ({
        userId: s.user_id ?? '',
        splitType: s.split_type ?? 'Equal',
        splitEnabled: s.split_enabled ?? true,
        shareMinor: moneyFromMajor(s.expense_amount ?? 0),
        amount: s.amount ?? 0,
        percentage: s.percentage ?? 0,
        shares: s.shares ?? 0,
      })),
    // Breakdown items (amounts INTEGER MINOR here → brand directly, do NOT scale). The wire sends
    // EVERY row as type "Item", so a saved Tip/Discount/Tax/Other charge is told apart by its
    // name / "-" sign (`classifyBreakdownRow`); `sing` (backend key) is the sign.
    breakdown: (dto.items ?? [])
      .filter((it) => (it.name ?? '') !== '' || (it.amount ?? 0) !== 0)
      .map((it) => ({
        name: it.name ?? '',
        amountMinor: money(Math.round(it.amount ?? 0)),
        type: classifyBreakdownRow({ type: it.type, name: it.name, sign: it.sing }),
        sign: it.sing === '-' ? ('-' as const) : ('+' as const),
      })),
    categoryName: dto.expense_type_name ?? '',
    isPrivate: dto.is_private,
    attachmentUrl: dto.attachment_url ?? '',
    groupId: dto.context_id ?? '',
    note: dto.notes ?? '',
    recurrence: toPendingRecurrence(dto.recurring_rules ?? dto.recurring ?? dto.recurrence),
    groupName: dto.context_name ?? '',
    isIncomplete,
    isNoGroup: dto.assign_status === 'unassigned',
    isArchived: dto.pending_status === 'archived',
    location: toPendingLocation(dto),
  };
}
